'use client';

import { useEffect, useState } from 'react';

interface ViewCounterProps {
  slug: string;
  category: string;
  increment?: boolean;
}

export default function ViewCounter({ slug, category, increment = true }: ViewCounterProps) {
  const [views, setViews] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fullSlug = `${category}/${slug}`;

    // Increment view count on mount if enabled
    if (increment) {
      fetch('/api/public/views', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: fullSlug }),
      })
        .then(res => res.json())
        .then(data => {
          setViews(data.views ?? 0);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to increment views:', err);
          // Fall back to GET
          fetchViews(fullSlug);
        });
    } else {
      fetchViews(fullSlug);
    }
  }, [slug, category, increment]);

  const fetchViews = async (fullSlug: string) => {
    try {
      const res = await fetch(`/api/public/views?slug=${encodeURIComponent(fullSlug)}`);
      const data = await res.json();
      setViews(data.views ?? 0);
    } catch (err) {
      console.error('Failed to fetch views:', err);
      setViews(0); // Show 0 on error instead of hiding
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-foreground-tertiary text-sm">
        <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <span>...</span>
      </div>
    );
  }

  if (views === null || views === undefined) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-foreground-tertiary text-sm" title={`${views.toLocaleString()} views`}>
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
      <span>{views.toLocaleString()} {views === 1 ? 'view' : 'views'}</span>
    </div>
  );
}
